import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { isMainPage, landingPathFor } from "@/config/navigation";
import { isNetworkError } from "@/lib/api/network-error";
import { clearOfflineSession } from "@/lib/api/offline-session";
import { clearRecentCommands } from "@/lib/recent-commands";
import { setActiveCountry, setActiveCurrency, setActivePrimaryColor } from "@/lib/utils";
import { useSiteStore } from "@/features/sites/store";

import { authApi } from "./api";
import { useAuthStore } from "./store";

export function useMe() {
  const setMe = useAuthStore((s) => s.setMe);
  const reset = useAuthStore((s) => s.reset);

  const query = useQuery({
    queryKey: ["auth", "me"],
    queryFn: authApi.me,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data) {
      setActiveCurrency(query.data.active_currency);
      setActiveCountry(query.data.active_country);
      setActivePrimaryColor(query.data.active_primary_color);
      setMe(query.data);
    } else if (query.isError && !isNetworkError(query.error)) {
      // Une coupure réseau ne vide PAS la session : le cookie JWT reste valide
      // côté serveur, seule la connectivité manque. Seul un refus du serveur
      // (401/403) justifie d'oublier l'utilisateur.
      reset();
      clearOfflineSession();
    }
  }, [query.data, query.isError, query.error, setMe, reset]);

  return query;
}

export function useLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: async (data) => {
      // 2FA active : pas de session encore, la page affiche l'étape « code ».
      if (data.mfa_required) return;
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(await postLoginTarget(queryClient));
    },
    // Pas de toast : l'erreur est déjà affichée en inline dans le formulaire
    // (LoginForm.loginError). Un toast ferait doublon.
  });
}

/**
 * Destination après connexion selon le type d'utilisateur : le personnel
 * plateforme (éditeur GTS) va sur la console `/editeur`, les autres sur le
 * tableau de bord clinique. Evite le détour éditeur → /dashboard →
 * sélection de clinique.
 */
async function postLoginTarget(queryClient: ReturnType<typeof useQueryClient>): Promise<string> {
  try {
    const me = await queryClient.fetchQuery({ queryKey: ["auth", "me"], queryFn: authApi.me });
    if (me.is_platform_staff || me.is_superuser) return "/editeur";
    return landingPathFor(me);
  } catch {
    return "/dashboard";
  }
}

export function useLoginVerify() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mfaToken, code }: { mfaToken: string; code: string }) =>
      authApi.loginVerify(mfaToken, code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(await postLoginTarget(queryClient));
    },
    // Pas de toast : l'erreur est déjà affichée en inline (LoginForm.verifyError).
  });
}

export function useMfaSetup() {
  return useMutation({ mutationFn: authApi.mfaSetup });
}

export function useMfaEnable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => authApi.mfaEnable(code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "me"] }),
  });
}

export function useMfaDisable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => authApi.mfaDisable(password),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "me"] }),
  });
}

export function useMfaRegenerateBackupCodes() {
  return useMutation({
    mutationFn: (password: string) => authApi.mfaRegenerateBackupCodes(password),
  });
}

/**
 * Ré-affichage des montants (confidentialité) : erreurs gérées par l'appelant
 * (401 mot de passe incorrect → inline ; 403 permission absente → toast).
 */
export function useVerifyAmounts() {
  return useMutation({
    mutationFn: (password: string) => authApi.verifyAmounts(password),
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const reset = useAuthStore((s) => s.reset);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      reset();
      useSiteStore.getState().reset();
      // Les commandes récentes sont locales au poste : un utilisateur qui se
      // déconnecte ne doit pas laisser ses habitudes au suivant.
      clearRecentCommands();
      // Idem pour l'instantané de session hors ligne (profil + file d'écritures) :
      // rien ne doit survivre à la déconnexion sur un poste partagé.
      clearOfflineSession();
      queryClient.clear();
      navigate("/login");
    },
  });
}

export function useChangePassword() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) =>
      authApi.changePassword(oldPassword, newPassword),
    onSuccess: async () => {
      // Rafraîchit `me` pour lever le verrou « changement forcé ».
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      toast.success("Mot de passe modifié.");
      navigate("/profil");
    },
    // Erreurs gérées par la page (affichées sous le champ concerné).
  });
}

export function useUpdateProfile() {
  const setMe = useAuthStore((s) => s.setMe);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (data) => {
      setMe(data);
      queryClient.setQueryData(["auth", "me"], data);
      toast.success("Profil mis à jour.");
    },
    // Erreurs de champ gérées par le formulaire appelant.
  });
}

export function useImpersonate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (userId: string) => authApi.impersonate(userId),
    onSuccess: async () => {
      // La session bascule sur l'utilisateur cible : on purge tout le cache
      // org-scopé et on recharge /me avant de rediriger.
      queryClient.clear();
      await queryClient.invalidateQueries();
      navigate("/dashboard");
    },
    onError: () => {
      toast.error("Impossible d'ouvrir cette session.");
    },
  });
}

export function useStopImpersonation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: authApi.stopImpersonation,
    onSuccess: async () => {
      queryClient.clear();
      toast.success("Retour à votre compte.");
      // Même destination qu'après une connexion : l'éditeur GTS (sans
      // clinique) retrouve sa console, pas l'écran de sélection de clinique.
      navigate(await postLoginTarget(queryClient));
    },
    onError: () => {
      toast.error("Impossible de revenir à votre compte.");
    },
  });
}

/** Cible d'une bascule vers « Toutes les cliniques » (ADR 0065) ; sinon l'id
 *  de la clinique. Un UUID ne peut pas valoir ce mot. */
export const ALL_CLINICS = "account";

export function useSwitchOrg() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (target: string) =>
      target === ALL_CLINICS ? authApi.switchToAllClinics() : authApi.switchOrganisation(target),
    // Bascule instantanée, sans rechargement de page :
    // 1. Le cookie JWT porte déjà la nouvelle clinique (réémis par switch-org).
    // 2. On rafraîchit `me` EN PLACE (refetch, pas de retrait) → l'utilisateur
    //    reste monté, pas de spinner plein écran de l'AuthGuard.
    // 3. On purge toutes les autres données (hors `auth`) : aucune donnée de
    //    l'ancienne clinique ne subsiste, même brièvement.
    // 4. Page principale (liste / racine de module) : on y RESTE, les données
    //    se rechargent en place. Page de détail ou de création : elle référence
    //    une entité de l'ancienne clinique → retour à l'accueil.
    onSuccess: async (_data, target) => {
      await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
      queryClient.removeQueries({
        predicate: (q) => !(Array.isArray(q.queryKey) && q.queryKey[0] === "auth"),
      });
      if (!isMainPage(window.location.pathname)) navigate("/dashboard");
      toast.success(
        target === ALL_CLINICS ? "Toutes les cliniques affichées." : "Clinique active changée.",
      );
    },
    onError: () => {
      toast.error("Impossible de changer de clinique (droits insuffisants ?)");
    },
  });
}
